import React, { useState } from 'react';
import { View, Text, StyleSheet, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { config } from '../src/config';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import Button from '../src/components/Button';
import Input from '../src/components/Input';
import ContentContainer from '../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';

/** ResetPasswordScreen - Set a new password using a token from the reset email link. */
export default function ResetPasswordScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const MIN_PASSWORD_LENGTH = 8;

  /** POST /api/auth/reset-password — submit new password with reset token. */
  const handleReset = async () => {
    if (!password || !confirmPassword) { setError('Please fill in all fields'); return; }
    if (password.length < MIN_PASSWORD_LENGTH) { setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`); return; }
    if (!/[A-Z]/.test(password)) { setError('Password must include an uppercase letter'); return; }
    if (!/[a-z]/.test(password)) { setError('Password must include a lowercase letter'); return; }
    if (!/[0-9]/.test(password)) { setError('Password must include a number'); return; }
    if (!/[^A-Za-z0-9]/.test(password)) { setError('Password must include a special character'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!token) { setError('Invalid reset link. Please request a new one.'); return; }

    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.scroll}>
          <ContentContainer maxWidth={480}>
            <Text style={styles.icon}>✅</Text>
            <Text style={styles.title}>Password reset!</Text>
            <Text style={styles.description}>
              Your password has been updated successfully. You can now sign in with your new password.
            </Text>
            <Button title="Sign In" onPress={() => router.replace('/login')} size="lg" style={styles.btn} />
          </ContentContainer>
        </ScrollView>
      </View>
    );
  }

  if (!token) {
    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.scroll}>
          <ContentContainer maxWidth={480}>
            <Text style={styles.icon}>⚠️</Text>
            <Text style={styles.title}>Invalid link</Text>
            <Text style={styles.description}>
              This password reset link is invalid or has expired. Please request a new one.
            </Text>
            <Button title="Request New Link" onPress={() => router.replace('/forgot-password')} size="lg" style={styles.btn} />
          </ContentContainer>
        </ScrollView>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <ContentContainer maxWidth={480}>
          <Text style={styles.title}>Set new password</Text>
          <Text style={styles.description}>
            Enter your new password below. Must be at least 8 characters with upper, lower, number, and symbol.
          </Text>

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <Input label="New Password" icon="lock-closed-outline" placeholder="8+ chars, upper, lower, number, symbol" value={password} onChangeText={setPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} maxLength={128} />
          <Input label="Confirm Password" icon="lock-closed-outline" placeholder="Confirm new password" value={confirmPassword} onChangeText={setConfirmPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} />
          <Button title="Reset Password" onPress={handleReset} loading={loading} size="lg" style={styles.btn} />
        </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, padding: spacing.lg, paddingTop: spacing.xl },
  icon: { fontSize: 48, textAlign: 'center', marginBottom: spacing.md },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  description: { fontSize: fontSize.md, color: c.textSecondary, lineHeight: 24, marginBottom: spacing.lg },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
  btn: { marginTop: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
